import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { User, Truck, Eye, EyeOff, Compass } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Login = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [userType, setUserType] = useState('carrier'); // carrier or shipper

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, null, {
        params: {
          email: email,
          password: password
        }
      });

      const userData = response.data.user;
      const userRoles = response.data.roles || userData.roles || [];
      
      // Debug logging - CRITICAL for troubleshooting
      console.log('=== LOGIN SUCCESS ===');
      console.log('Full Response:', response.data);
      console.log('User Data:', userData);
      console.log('User Roles:', userRoles);
      console.log('Selected Type:', userType);
      console.log('Email:', userData.email);
      
      // Store user data and roles in localStorage
      localStorage.setItem('user_email', userData.email);
      localStorage.setItem('user_roles', JSON.stringify(userRoles));
      localStorage.setItem('user_data', JSON.stringify(userData));
      localStorage.setItem('active_role', userType);
      
      console.log('=== LOCALSTORAGE SET ===');
      console.log('user_email:', localStorage.getItem('user_email'));
      console.log('user_roles:', localStorage.getItem('user_roles'));
      console.log('active_role:', localStorage.getItem('active_role'));

      // Check if user has the selected role
      if (userRoles.length > 0 && !userRoles.includes(userType)) {
        // User doesn't have the selected role - redirect to their primary role
        const primaryRole = userRoles[0];
        console.log(`Role mismatch - user has ${userRoles}, but selected ${userType}. Redirecting to ${primaryRole}`);
        setError(`This account doesn't have a ${userType} role. Redirecting to your ${primaryRole} dashboard...`);
        
        // Immediate redirect with email parameter
        if (primaryRole === 'carrier' || primaryRole === 'crane_operator') {
          window.location.href = `/carrier-dashboard?email=${encodeURIComponent(email)}`;
        } else if (primaryRole === 'shipper') {
          window.location.href = `/shipper-dashboard?email=${encodeURIComponent(email)}`;
        }
        return;
      }

      // Redirect based on selected user type - use window.location for hard navigation
      console.log(`=== REDIRECTING TO ${userType} DASHBOARD ===`);
      if (userType === 'carrier' || userType === 'crane_operator') {
        window.location.href = `/carrier-dashboard?email=${encodeURIComponent(email)}`;
      } else if (userType === 'shipper') {
        window.location.href = `/shipper-dashboard?email=${encodeURIComponent(email)}`;
      } else {
        setError('Invalid user role');
      }
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Invalid email or password');
      } else if (err.response?.status === 403) {
        setError('Your account has been deactivated. Please contact support.');
      } else {
        setError('Login failed. Please try again.');
      }
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="container mx-auto px-4 sm:px-6 py-12">
        <div className="max-w-md mx-auto">
          <h1 className="text-3xl font-bold text-[#1e3a5f] text-center mb-8">
            Log In to Truckn Send It
          </h1>

          {/* User Type Selection */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            <Card
              onClick={() => setUserType('carrier')}
              className={`p-4 cursor-pointer transition-all text-center ${
                userType === 'carrier'
                  ? 'border-2 border-red-600 bg-red-50'
                  : 'border border-gray-200 hover:border-gray-300'
              }`}
              data-testid="login-role-carrier"
            >
              <Truck className={`w-7 h-7 mx-auto mb-2 ${
                userType === 'carrier' ? 'text-red-600' : 'text-gray-500'
              }`} />
              <p className={`font-semibold text-sm ${
                userType === 'carrier' ? 'text-red-600' : 'text-gray-700'
              }`}>
                Carrier
              </p>
              <p className="text-xs text-gray-600 mt-1">Find &amp; quote on loads</p>
            </Card>

            <Card
              onClick={() => setUserType('crane_operator')}
              className={`p-4 cursor-pointer transition-all text-center ${
                userType === 'crane_operator'
                  ? 'border-2 border-red-600 bg-red-50'
                  : 'border border-gray-200 hover:border-gray-300'
              }`}
              data-testid="login-role-crane-operator"
            >
              <Truck className={`w-7 h-7 mx-auto mb-2 ${
                userType === 'crane_operator' ? 'text-red-600' : 'text-gray-500'
              }`} />
              <p className={`font-semibold text-sm ${
                userType === 'crane_operator' ? 'text-red-600' : 'text-gray-700'
              }`}>
                Crane Operator
              </p>
              <p className="text-xs text-gray-600 mt-1">Crane hire services</p>
            </Card>

            <Card
              onClick={() => setUserType('shipper')}
              className={`p-4 cursor-pointer transition-all text-center ${
                userType === 'shipper'
                  ? 'border-2 border-red-600 bg-red-50'
                  : 'border border-gray-200 hover:border-gray-300'
              }`}
              data-testid="login-role-shipper"
            >
              <User className={`w-7 h-7 mx-auto mb-2 ${
                userType === 'shipper' ? 'text-red-600' : 'text-gray-500'
              }`} />
              <p className={`font-semibold text-sm ${
                userType === 'shipper' ? 'text-red-600' : 'text-gray-700'
              }`}>
                Shipper
              </p>
              <p className="text-xs text-gray-600 mt-1">Post loads &amp; get quotes</p>
            </Card>

            <Card
              onClick={() => navigate('/pilot-dashboard')}
              className="p-4 cursor-pointer transition-all text-center border border-gray-200 hover:border-red-300 hover:bg-red-50/40"
              data-testid="login-role-pilot"
            >
              <Compass className="w-7 h-7 mx-auto mb-2 text-gray-500" />
              <p className="font-semibold text-sm text-gray-700">
                Pilot / Steerer
              </p>
              <p className="text-xs text-gray-600 mt-1">Escort &amp; steering pilots</p>
            </Card>
          </div>

          <Card className="p-8">
            <form onSubmit={handleLogin} className="space-y-4">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                  {error}
                </div>
              )}

              <div>
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={userType === 'carrier' ? 'carrier@example.com' : 'shipper@example.com'}
                  required
                  disabled={loading}
                />
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <Label htmlFor="password">Password</Label>
                  <button
                    type="button"
                    onClick={() => navigate('/forgot-password')}
                    className="text-sm text-blue-600 hover:text-blue-700"
                  >
                    Forgot Password?
                  </button>
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    required
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-red-600 hover:bg-red-700 text-white"
                disabled={loading}
              >
                {loading ? 'Logging in...' : `Log In as ${userType === 'carrier' ? 'Carrier' : userType === 'crane_operator' ? 'Crane Operator' : 'Shipper'}`}
              </Button>

              <div className="text-center pt-4 border-t">
                <p className="text-sm text-gray-600 mb-2">
                  Don&apos;t have an account?
                </p>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(`/signup?role=${userType}`)}
                  className="w-full"
                  disabled={loading}
                >
                  Sign Up as {userType === 'carrier' ? 'Carrier' : userType === 'crane_operator' ? 'Crane Operator' : 'Shipper'}
                </Button>
              </div>
            </form>
          </Card>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default Login;
